<script lang="ts">
  import { onMount } from 'svelte';
  import { getMyStats, getLeaderboard, type MyStatsResponse, type LeaderboardResponse } from '../lib/api';
  import { currentUser } from '../lib/stores/auth';

  let stats: MyStatsResponse | null = null;
  let leaderboard: LeaderboardResponse | null = null;
  let error: string | null = null;

  /** Fetch stats + leaderboard. Called by onMount in prod; called directly by tests
   * because Svelte 4 onMount doesn't fire in this project's jsdom test env (see
   * Map.test.ts and RecordContact.test.ts notes). */
  export async function load(): Promise<void> {
    try {
      const [s, lb] = await Promise.all([getMyStats(), getLeaderboard()]);
      stats = s;
      leaderboard = lb;
    } catch (e) {
      error = 'Failed to load stats.';
      console.error(e);
    }
  }

  onMount(() => { void load(); });

  $: meId = $currentUser?.id;
</script>

<div class="stats" data-testid="volunteer-stats">
  {#if error}
    <p class="err">{error}</p>
  {:else if stats}
    <section class="block">
      <h3>This week</h3>
      <div class="numbers">
        <div><strong>{stats.this_week.total}</strong><span>contacts</span></div>
        <div><strong>{stats.this_week.spoke}</strong><span>spoke with</span></div>
        <div><strong>{stats.this_week.supporters_id}</strong><span>supporters ID'd</span></div>
      </div>
    </section>
    <section class="block">
      <h3>All time</h3>
      <div class="numbers">
        <div><strong>{stats.all_time.total}</strong><span>contacts</span></div>
        <div><strong>{stats.all_time.spoke}</strong><span>spoke with</span></div>
        <div><strong>{stats.all_time.supporters_id}</strong><span>supporters ID'd</span></div>
      </div>
    </section>
    {#if leaderboard && leaderboard.this_week.length > 0}
      <section class="block">
        <h3>Top this week</h3>
        <ol class="board">
          {#each leaderboard.this_week as row, i (row.user_id)}
            <li class:me={row.user_id === meId}>
              <span class="rank">{i + 1}</span>
              <span class="name">{row.display_name}</span>
              <span class="total">{row.total}</span>
            </li>
          {/each}
        </ol>
      </section>
    {/if}
  {:else}
    <p class="loading">Loading…</p>
  {/if}
</div>

<style>
  .stats { padding: var(--s-4); display: flex; flex-direction: column; gap: var(--s-4); min-width: 320px; }
  .block h3 {
    font-size: 0.78rem; text-transform: uppercase; letter-spacing: 0.12em;
    font-weight: 600; color: var(--ink-soft); margin: 0 0 var(--s-2);
  }
  .numbers { display: flex; gap: var(--s-4); }
  .numbers div { display: flex; flex-direction: column; min-width: 70px; }
  .numbers strong { font-family: var(--font-display); font-size: 1.8rem; line-height: 1; color: var(--ink); }
  .numbers span { font-size: 0.78rem; color: var(--ink-soft); }
  .board { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 4px; }
  .board li { display: grid; grid-template-columns: 1.5em 1fr auto; align-items: baseline; padding: 4px 0; border-bottom: 1px dotted var(--rule); }
  .board .rank { color: var(--ink-soft); font-family: var(--font-mono); }
  .board .total { font-family: var(--font-mono); }
  .board li.me { background: var(--leaf-soft); padding-left: 6px; padding-right: 6px; }
  .err { color: var(--alert); }
  .loading { color: var(--ink-soft); }
</style>
